import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { CreditCard, Lock, AlertTriangle } from 'lucide-react';
import { getAdminPaymentConfig, updateAdminPaymentConfig } from '../../redux/paymentSlice';

const TIMING_OPTIONS = [
    { value: 'postpaid', label: 'After the visit' },
    { value: 'prepaid', label: 'Before the visit' },
    { value: 'both', label: 'Either way' },
];

// ONE HOSPITAL's own payment configuration: which methods it accepts and when
// patients pay. Was a single global row shared by every hospital — one admin
// turning off Cash turned it off for all of them — now each hospital writes its
// own row and inherits the platform default until it does.
//
// A method the platform has put ON HOLD (gateway outage) is shown greyed with
// the reason, and its switch is left alone: the hold is a temporary cover, so
// when it is released the hospital's own choice comes back by itself.
export default function AdminPaymentsTab() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const adminConfig = useSelector((s) => s.payment?.adminConfig);

    const [methods, setMethods] = useState([]);
    const [timingPolicy, setTimingPolicy] = useState('postpaid');
    const [saving, setSaving] = useState(false);

    useEffect(() => { dispatch(getAdminPaymentConfig()); }, [dispatch]);

    useEffect(() => {
        if (adminConfig) {
            setMethods(adminConfig.methods || []);
            setTimingPolicy(adminConfig.timingPolicy || 'postpaid');
        }
    }, [adminConfig]);

    const toggleMethod = (key) => {
        setMethods((prev) => prev.map((m) => (m.key === key ? { ...m, enabled: !m.enabled } : m)));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await dispatch(updateAdminPaymentConfig({ methods, timingPolicy })).unwrap();
            toast.success(t('settings.updated', 'Setting updated'));
        } catch (err) {
            toast.error(typeof err === 'string' ? err : t('settings.updateFailed', 'Failed to update setting'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="admin-payments-tab" className="space-y-6">
            {/* Methods */}
            <div className="rounded-2xl border border-gray-200">
                <div className="flex items-center gap-2 p-4 border-b border-gray-100">
                    <CreditCard className="w-4 h-4 text-[#4ABDBD]" />
                    <h3 className="font-semibold text-gray-800">{t('payments.methods', 'Payment methods')}</h3>
                </div>
                <div className="divide-y divide-gray-100">
                    {methods.map((m) => (
                        <div key={m.key} data-testid={`admin-payment-method-${m.key}`} className="flex items-center justify-between gap-4 p-4">
                            <div className="min-w-0">
                                <div className="font-medium text-gray-800 text-sm flex items-center gap-2 flex-wrap">
                                    {m.label}
                                    {m.requiresGateway && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                            <Lock className="w-3 h-3" /> {t('payments.needs_gateway', 'Needs gateway (Phase 2)')}
                                        </span>
                                    )}
                                    {m.heldByPlatform && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                                            <AlertTriangle className="w-3 h-3" />
                                            {t('payments.on_hold', 'ON HOLD')}
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">{t('payments.method_timing', 'Timing')}: {m.supportedTiming}</p>
                                {/* Says it is a platform issue, not their misconfiguration —
                                    which is what stops the support ticket. */}
                                {m.heldByPlatform && (
                                    <p className="text-xs text-red-700 mt-1">
                                        {t('payments.held_notice', 'Temporarily unavailable — platform maintenance')}
                                        {m.holdReason ? `: ${m.holdReason}` : ''}
                                    </p>
                                )}
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={!!m.enabled}
                                    onChange={() => toggleMethod(m.key)}
                                    data-testid={`admin-payment-toggle-${m.key}`}
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#4ABDBD] rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
                            </label>
                        </div>
                    ))}
                </div>
            </div>

            {/* Timing policy */}
            <div className="rounded-2xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-800 mb-1">{t('payments.timing_policy', 'Payment timing policy')}</h3>
                <p className="text-xs text-gray-500 mb-3">{t('payments.timing_policy_desc', 'Doctors can only choose within this.')}</p>
                <div className="flex flex-col sm:flex-row gap-2">
                    {TIMING_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => setTimingPolicy(opt.value)}
                            data-testid={`admin-payment-timing-${opt.value}`}
                            className={`flex-1 text-sm px-4 py-2.5 rounded-xl border transition ${timingPolicy === opt.value ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#3AA8A8] font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                        >
                            {t(`payments.timing_${opt.value}`, opt.label)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="admin-payments-save"
                    className={`px-6 py-2.5 rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transition ${saving ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg'}`}
                >
                    {saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
                </button>
            </div>
        </div>
    );
}
